import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { OrdersChart } from "../charts";
import { Box, Heading, Icon, Item, List, Text } from "../elements";
import { CircularProgress, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ExpenseCategories } from "../../utils/types/expenseTypes";
import { formatAmountSuffix } from "../../utils/formatters";
import { AnalyticsService } from "../../services";

export function CategorySummaryChart({ fromDate, toDate }) {
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const userDetails = useSelector((state) => state.user?.user);
  const companyId = userDetails?.company?.id;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await AnalyticsService.getCategorySummary({
        page: 1,
        // High cap: the endpoint returns the full aggregated set; this just satisfies
        // the required page/limit params without silently truncating categories.
        limit: 1000,
        ...(companyId ? { companyId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      });
      setCategoryData(data || []);
    } catch (error) {
      console.error("🚀 ~ CategorySummary ~ fetchData ~ error:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Predefined chart colors matching OrdersChart
  const COLORS = ["#0361cc", "#3dabc7", "#2eb367", "#da9412", "#edb213", "#0A3D62"];
  const ICONS = ["receipt", "engineering", "groups", "construction", "account_balance_wallet", "bar_chart"];

  const categoryMap = {
    [ExpenseCategories.WORKER]: "Worker",
    SUBCONTRACTOR_PAYMENT: "Subcontractor Payment",
    [ExpenseCategories.EMPLOYEE]: "Employee",
    [ExpenseCategories.VENDOR]: "Materials",
    [ExpenseCategories.SUBCONTRACTOR]: "Subcontractor",
  };

  const chartData = useMemo(() => {
    const processedDataMap = {};
    const otherExpensesList = [];
    let otherExpenseTotal = 0;

    categoryData.forEach((item) => {
      const expense = parseFloat(item.totalExpense || 0);
      const categoryKey = item.categoryName;

      if (categoryMap[categoryKey]) {
        const displayName = categoryMap[categoryKey];
        processedDataMap[displayName] = (processedDataMap[displayName] || 0) + expense;
      } else {
        otherExpenseTotal += expense;
        if (expense > 0) {
          otherExpensesList.push({ name: categoryKey, value: expense });
        }
      }
    });

    const aggregatedData = Object.entries(processedDataMap).map(([name, value]) => ({
      name,
      value,
    }));

    if (otherExpenseTotal > 0) {
      aggregatedData.push({
        name: "Other Expense",
        value: otherExpenseTotal,
        isOther: true,
        subItems: otherExpensesList.sort((a, b) => b.value - a.value),
      });
    }

    // Sort descending by value
    aggregatedData.sort((a, b) => b.value - a.value);

    // Map to chart properties
    return aggregatedData.map((item, index) => ({
      ...item,
      color: COLORS[index % COLORS.length],
      icon: ICONS[index % ICONS.length],
    }));
  }, [categoryData]);

  return (
    <Box
      className="mc-card"
      sx={{
        height: "100%",
        maxHeight: 480, // Matches ProjectSummaryChart total height
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        m: 0,
      }}
    >
      <Box className="mc-card-header" style={{ display: "flex", justifyContent: "center" }}>
        <Heading as="h4" className="mc-card-title" style={{ fontSize: "20px", fontWeight: 600, paddingBottom: "10px" }}>
          Expense Summary
        </Heading>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : chartData.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          {/* Pie/Donut Chart Container */}
          <Box sx={{ height: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <OrdersChart chart={chartData} paddingAngle={0} tooltipFormatter={(value) => formatAmountSuffix(value, true)} />
          </Box>

          {/* Scrollable List to match height constraint */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: 0.5,
              mt: 1,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "4px" },
            }}
          >
            <List className="mc-order-card-list">
              {chartData.map((item) => (
                <React.Fragment key={item.name}>
                  <Item
                    className="mc-order-card-item"
                    style={{
                      cursor: item.isOther ? "pointer" : "default",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                    }}
                    onClick={() => item.isOther && setExpanded(!expanded)}
                    {...(item.isOther && {
                      role: "button",
                      tabIndex: 0,
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setExpanded(!expanded);
                        }
                      },
                    })}
                  >
                    <Icon style={{ color: item.color, fontSize: "18px" }} className="material-icons">
                      {item.icon}
                    </Icon>
                    <Text
                      as="p"
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginRight: "8px",
                        marginLeft: "8px",
                        fontSize: "13px",
                      }}
                    >
                      {item.name}
                      {item.isOther && (
                        <span style={{ display: "inline-flex", marginLeft: "4px" }}>
                          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </span>
                      )}
                    </Text>
                    <Heading as="h6" style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                      {formatAmountSuffix(item.value)}
                    </Heading>
                  </Item>

                  {/* Expanded Sub-items for 'Other Expense' */}
                  {item.isOther && item.subItems && (
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {item.subItems.map((subItem) => (
                          <Item
                            key={subItem.name}
                            className="mc-order-card-item"
                            style={{
                              paddingLeft: "10px",
                              borderBottom: "none",
                              paddingTop: "4px",
                              paddingBottom: "4px",
                            }}
                          >
                            <Icon style={{ color: "var(--text)", fontSize: "14px" }} className="material-icons">
                              subdirectory_arrow_right
                            </Icon>
                            <Text as="p" style={{ fontSize: "11px" }}>
                              {subItem.name}
                            </Text>
                            <Heading as="h5" style={{ fontSize: "11px" }}>
                              {formatAmountSuffix(subItem.value, true)}
                            </Heading>
                          </Item>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Box>
      ) : (
        <Box sx={{ py: 6, textAlign: "center", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text>No category data available</Text>
        </Box>
      )}
    </Box>
  );
}
